/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useMemo } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import Icon from "@mui/material/Icon";
import Card from "@mui/material/Card";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDBadgeDot from "components/MDBadgeDot";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDDatePicker from "components/MDDatePicker";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DefaultStatisticsCard from "examples/Cards/StatisticsCards/DefaultStatisticsCard";
import DefaultLineChart from "examples/Charts/LineCharts/DefaultLineChart";
// (removed) HorizontalBarChart & SalesTable per permintaan
import DataTable from "examples/Tables/DataTable";

import { logoutUser } from "actions/AuthAction";
import { useNavigate } from "react-router-dom";

// Proyek dashboard components
import StatusProyekChart from "layouts/dashboards/proyek/components/StatusProyekChart";

// Util format angka Rupiah
const formatRupiah = (num) => {
  if (num === null || num === undefined) return "Rp 0";
  try {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      maximumFractionDigits: 0,
    }).format(Number(num));
  } catch (e) {
    return `Rp ${Number(num).toLocaleString("id-ID")}`;
  }
};

function Proyek() {
  // DefaultStatisticsCard state for the dropdown value
  const [proyekDropdownValue, setProyekDropdownValue] = useState("Bulan Ini");
  const [nilaiDropdownValue, setNilaiDropdownValue] = useState("Bulan Ini");
  const [progressDropdownValue, setProgressDropdownValue] =
    useState("Bulan Ini");
  const navigate = useNavigate();

  // DefaultStatisticsCard state for the dropdown action
  const [proyekDropdown, setProyekDropdown] = useState(null);
  const [nilaiDropdown, setNilaiDropdown] = useState(null);
  const [progressDropdown, setProgressDropdown] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({
    totalProyek: 0,
    totalNilai: 0,
    rataProgress: 0,
  });
  const [totalTenagaKerja, setTotalTenagaKerja] = useState(0);
  const [progressBulanan, setProgressBulanan] = useState({
    labels: [],
    datasets: [],
  });
  // (removed) progressPerLokasi per permintaan
  const [queryDate, setQueryDate] = useState("2025-09-30");

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (!user) {
      logoutUser(navigate);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    async function fetchData() {
      try {
        setLoading(true);
        setError("");
        const res = await fetch(
          `https://hoholok.rotendaokab.go.id/api/laporan-harian/rekap-progres?tanggal=${encodeURIComponent(
            queryDate
          )}`,
          { signal: controller.signal }
        );
        if (!res.ok) throw new Error(`Gagal memuat data (${res.status})`);
        const json = await res.json();
        const data = Array.isArray(json?.data) ? json.data : [];

        // Tabel
        const mappedRows = data.map((d) => ({
          nama_proyek: d.nama_proyek,
          lokasi: d.lokasi,
          nilai_proyek: formatRupiah(d.nilai_proyek || 0),
          target: `${(d.target_persen ?? 0).toFixed(2)}%`,
          realisasi: `${(d.realisasi_persen ?? 0).toFixed(2)}%`,
          deviasi: `${(d.deviasi ?? 0).toFixed(2)}%`,
          kontraktor: d.nama_kontraktor_pelaksana || "-",
        }));
        setRows(mappedRows);

        // Ringkasan
        const totalProyek = data.length;
        const totalNilai = data.reduce(
          (acc, d) => acc + Number(d.nilai_proyek || 0),
          0
        );
        const rataProgress = totalProyek
          ? data.reduce((acc, d) => acc + Number(d.realisasi_persen || 0), 0) /
            totalProyek
          : 0;
        setSummary({ totalProyek, totalNilai, rataProgress });
        const tenaga = data.reduce(
          (acc, d) => acc + Number(d.total_tenaga_kerja || 0),
          0
        );
        setTotalTenagaKerja(tenaga);

        // Progress bulanan/weekly chart dari proyek pertama yang punya data mingguan
        const withWeekly = data.find(
          (d) => Array.isArray(d.mingguan) && d.mingguan.length > 0
        );
        if (withWeekly) {
          const labels = withWeekly.mingguan.map((m) => `M${m.minggu_ke}`);
          const targetData = withWeekly.mingguan.map((m) =>
            Number(m.target_persen_kumulatif || 0)
          );
          const realisasiData = withWeekly.mingguan.map((m) =>
            Number(m.realisasi_persen_kumulatif || 0)
          );
          setProgressBulanan({
            labels,
            datasets: [
              { label: "Target (%)", color: "info", data: targetData },
              { label: "Realisasi (%)", color: "success", data: realisasiData },
            ],
          });
        } else {
          setProgressBulanan({ labels: [], datasets: [] });
        }

        // (removed) Progress rata-rata per lokasi
      } catch (err) {
        if (err.name !== "AbortError")
          setError(err.message || "Terjadi kesalahan");
      } finally {
        setLoading(false);
      }
    }
    fetchData();
    return () => controller.abort();
  }, [queryDate]);

  // DefaultStatisticsCard handler for the dropdown action
  const openProyekDropdown = ({ currentTarget }) =>
    setProyekDropdown(currentTarget);
  const closeProyekDropdown = ({ currentTarget }) => {
    setProyekDropdown(null);
    setProyekDropdownValue(currentTarget.innerText || proyekDropdownValue);
  };
  const openNilaiDropdown = ({ currentTarget }) =>
    setNilaiDropdown(currentTarget);
  const closeNilaiDropdown = ({ currentTarget }) => {
    setNilaiDropdown(null);
    setNilaiDropdownValue(currentTarget.innerText || nilaiDropdownValue);
  };
  const openProgressDropdown = ({ currentTarget }) =>
    setProgressDropdown(currentTarget);
  const closeProgressDropdown = ({ currentTarget }) => {
    setProgressDropdown(null);
    setProgressDropdownValue(currentTarget.innerText || progressDropdownValue);
  };

  // Dropdown menu template for the DefaultStatisticsCard
  const renderMenu = (state, close) => (
    <Menu
      anchorEl={state}
      transformOrigin={{ vertical: "top", horizontal: "center" }}
      open={Boolean(state)}
      onClose={close}
      keepMounted
      disableAutoFocusItem
    >
      <MenuItem onClick={close}>Bulan Ini</MenuItem>
      <MenuItem onClick={close}>3 Bulan Terakhir</MenuItem>
      <MenuItem onClick={close}>6 Bulan Terakhir</MenuItem>
      <MenuItem onClick={close}>Tahun Ini</MenuItem>
    </Menu>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox mb={3}>
          <Grid container alignItems="center" spacing={2}>
            <Grid item xs={12} md={7}>
              <MDBox>
                <MDTypography variant="h4" fontWeight="bold">
                  Dashboard Proyek
                </MDTypography>
                <MDTypography variant="button" color="text">
                  Rekap progres pekerjaan per tanggal
                </MDTypography>
              </MDBox>
            </Grid>
            <Grid item xs={12} md={5}>
              <MDBox display="flex" justifyContent="flex-end" gap={1}>
                <MDDatePicker
                  input={{ placeholder: "Pilih tanggal" }}
                  value={queryDate}
                  onChange={(val) => {
                    // val bisa berupa string tanggal atau array dari komponen
                    const first = Array.isArray(val) ? val[0] : val;
                    if (!first) return;
                    try {
                      const d = new Date(first);
                      const yyyy = d.getFullYear();
                      const mm = String(d.getMonth() + 1).padStart(2, "0");
                      const dd = String(d.getDate()).padStart(2, "0");
                      setQueryDate(`${yyyy}-${mm}-${dd}`);
                    } catch (_) {
                      // abaikan parsing error
                    }
                  }}
                />
                <MDButton
                  variant="gradient"
                  color="info"
                  onClick={() => setQueryDate((d) => d)}
                >
                  <Icon>refresh</Icon>&nbsp;Muat Ulang
                </MDButton>
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
        <MDBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <DefaultStatisticsCard
                title="Total Proyek Aktif"
                count={summary.totalProyek.toString()}
                percentage={{
                  color: "success",
                  value: "+3",
                  label: "dari bulan lalu",
                }}
                dropdown={{
                  action: openProyekDropdown,
                  menu: renderMenu(proyekDropdown, closeProyekDropdown),
                  value: proyekDropdownValue,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DefaultStatisticsCard
                title="Total Nilai Proyek"
                count={formatRupiah(summary.totalNilai)}
                percentage={{
                  color: "success",
                  value: "+12%",
                  label: "dari bulan lalu",
                }}
                dropdown={{
                  action: openNilaiDropdown,
                  menu: renderMenu(nilaiDropdown, closeNilaiDropdown),
                  value: nilaiDropdownValue,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DefaultStatisticsCard
                title="Rata-rata Progress"
                count={`${summary.rataProgress.toFixed(2)}%`}
                percentage={{
                  color: "success",
                  value: "+5.2%",
                  label: "dari bulan lalu",
                }}
                dropdown={{
                  action: openProgressDropdown,
                  menu: renderMenu(progressDropdown, closeProgressDropdown),
                  value: progressDropdownValue,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DefaultStatisticsCard
                title="Total Tenaga Kerja"
                count={totalTenagaKerja.toString()}
                percentage={{
                  color: "info",
                  value: "",
                  label: "saat ini",
                }}
                dropdown={{
                  action: openProyekDropdown,
                  menu: renderMenu(proyekDropdown, closeProyekDropdown),
                  value: proyekDropdownValue,
                }}
              />
            </Grid>
          </Grid>
        </MDBox>
        {loading && (
          <MDBox mb={3}>
            <Card>
              <MDBox p={3}>
                <MDTypography variant="button" color="text">
                  Memuat data...
                </MDTypography>
              </MDBox>
            </Card>
          </MDBox>
        )}
        <MDBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} lg={4}>
              <StatusProyekChart />
            </Grid>
            <Grid item xs={12} sm={6} lg={8}>
              <DefaultLineChart
                title="Progress Realisasi vs Target"
                description={
                  <MDBox display="flex" justifyContent="space-between">
                    <MDBox display="flex" ml={-1}>
                      <MDBadgeDot
                        color="info"
                        size="sm"
                        badgeContent="Target"
                      />
                      <MDBadgeDot
                        color="success"
                        size="sm"
                        badgeContent="Realisasi"
                      />
                    </MDBox>
                    <MDBox mt={-4} mr={-1} position="absolute" right="1.5rem">
                      <Tooltip
                        title="Lihat perbandingan target vs realisasi"
                        placement="left"
                        arrow
                      >
                        <MDButton
                          variant="outlined"
                          color="secondary"
                          size="small"
                          circular
                          iconOnly
                        >
                          <Icon>priority_high</Icon>
                        </MDButton>
                      </Tooltip>
                    </MDBox>
                  </MDBox>
                }
                chart={progressBulanan}
              />
            </Grid>
          </Grid>
        </MDBox>
        {/* (removed) Top Proyek dan Progress per Kecamatan */}
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox pt={3} px={3}>
                <MDTypography variant="h6" fontWeight="medium">
                  Daftar Proyek Aktif
                </MDTypography>
                {error && (
                  <MDTypography variant="button" color="error">
                    {error}
                  </MDTypography>
                )}
              </MDBox>
              <MDBox py={1}>
                <DataTable
                  table={{
                    columns: [
                      { Header: "Nama Proyek", accessor: "nama_proyek" },
                      { Header: "Lokasi", accessor: "lokasi" },
                      { Header: "Nilai Proyek", accessor: "nilai_proyek" },
                      { Header: "Target", accessor: "target" },
                      { Header: "Realisasi", accessor: "realisasi" },
                      { Header: "Deviasi", accessor: "deviasi" },
                      { Header: "Kontraktor", accessor: "kontraktor" },
                    ],
                    rows: rows,
                  }}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  isSorted={false}
                  noEndBorder
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Proyek;
